import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import styled from 'styled-components/native'

import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { RecordSessionProps } from '../../hooks/useZoom/types'
import { RecordingConsentInfo } from '../../molecules/recordingConsentInfo/RecordingConsentInfo'
import { TitleSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'

const RECORDING_NUDGE_DELAY_MS = 10 * 60 * 1000

const Container = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: `${theme.spacing['16px']} ${theme.spacing['48px']}`,
  boxShadow: `0 2px 10px ${theme.colors.shadowLow}`,
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
}))

const LeftContainer = styled.View({})

const ButtonContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

export type RecordingConsentBannerProps = RecordSessionProps
export const RecordingConsentBanner: FunctionComponent<RecordingConsentBannerProps> = ({
  recordSession,
  dontRecordSession,
  dontRecordEpisode,
}) => {
  const [isDontRecordModalVisible, setIsDontRecordModalVisible] = useState(false)
  const [isRecordSessionLoading, setIsRecordSessionLoading] = useState(false)
  const [isDontRecordSessionLoading, setIsDontRecordSessionLoading] = useState(false)
  const [isDontRecordEpisodeLoading, setIsDontRecordEpisodeLoading] = useState(false)
  const [isWhatToMentionOpen, setIsWhatToMentionOpen] = useState(false)
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelNudgeTimer = useCallback(() => {
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current)
      nudgeTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    nudgeTimerRef.current = setTimeout(() => {
      setIsWhatToMentionOpen(true)
      nudgeTimerRef.current = null
    }, RECORDING_NUDGE_DELAY_MS)
    return cancelNudgeTimer
  }, [cancelNudgeTimer])

  const handlePopoverOpenChange = useCallback((isOpen: boolean) => {
    setIsWhatToMentionOpen(isOpen)
  }, [])

  return (
    <>
      <Container testID={tID('RecordingConsentBanner')}>
        <LeftContainer>
          <Title
            text={
              <FormattedMessage
                defaultMessage='Remember to get consent for recording.'
                description='Text for recording consent banner'
              />
            }
            size={TitleSize.SMALL}
          />
          <RecordingConsentInfo isPopoverOpen={isWhatToMentionOpen} setIsPopoverOpen={handlePopoverOpenChange} />
        </LeftContainer>
        <ButtonContainer>
          <TertiaryButton
            testID={tID('RecordingConsentBanner-dont-record-button')}
            text={
              <FormattedMessage defaultMessage="Don't record" description='Button text to not record video session' />
            }
            onPress={() => {
              cancelNudgeTimer()
              setIsDontRecordModalVisible(true)
            }}
          />
          <PrimaryButton
            testID={tID('RecordingConsentBanner-record-button')}
            text={<FormattedMessage defaultMessage='Record' description='Button text to record video session' />}
            onPress={() => {
              cancelNudgeTimer()
              setIsRecordSessionLoading(true)
              recordSession().finally(() => setIsRecordSessionLoading(false))
            }}
            loading={isRecordSessionLoading}
            disabled={isDontRecordEpisodeLoading || isDontRecordSessionLoading}
          />
        </ButtonContainer>
      </Container>
      <ConfirmationModal
        visible={isDontRecordModalVisible}
        onRequestClose={() => setIsDontRecordModalVisible(false)}
        confirmButtonLoading={isDontRecordSessionLoading}
        cancelButtonLoading={isDontRecordEpisodeLoading}
        confirmButtonDisabled={isDontRecordEpisodeLoading}
        cancelButtonDisabled={isDontRecordSessionLoading}
        testID={tID('RecordingConsentBanner-modal')}
        onConfirmationButtonPress={() => {
          setIsDontRecordSessionLoading(true)
          dontRecordSession().finally(() => {
            setIsDontRecordSessionLoading(false)
            setIsDontRecordModalVisible(false)
          })
        }}
        onCancelPressed={() => {
          setIsDontRecordEpisodeLoading(true)
          dontRecordEpisode().finally(() => {
            setIsDontRecordEpisodeLoading(false)
            setIsDontRecordModalVisible(false)
          })
        }}
        modalTitle={
          <FormattedMessage
            defaultMessage='Turn off recordings for just this session or the entire episode?'
            description='Title of modal to confirm turning off recordings'
          />
        }
        modalContents={
          <FormattedMessage
            defaultMessage='You won’t be able to turn recordings back on once they are disabled.'
            description='Text for modal to confirm turning off recordings'
          />
        }
        confirmationButtonText='Just this session'
        cancelButtonText='Entire episode'
      />
    </>
  )
}
